import React, { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  dismissA2hs,
  getA2hsPlatform,
  getDeferredInstallPrompt,
  isStandaloneDisplay,
  listenForInstallPrompt,
  promptInstall,
  shouldShowA2hsBanner,
} from "@/lib/addToHomeScreen";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

function InstallSteps({ platform }) {
  if (platform === "ios") {
    return (
      <ol className="list-decimal pl-5 space-y-2 text-sm text-muted-foreground">
        <li>
          Tap the <Share className="inline h-3.5 w-3.5 mx-0.5" /> Share button
          in Safari (or your browser’s share menu).
        </li>
        <li>Scroll and tap <span className="font-medium text-foreground">Add to Home Screen</span>.</li>
        <li>Confirm with <span className="font-medium text-foreground">Add</span>.</li>
      </ol>
    );
  }
  if (platform === "android") {
    return (
      <ol className="list-decimal pl-5 space-y-2 text-sm text-muted-foreground">
        <li>Tap <span className="font-medium text-foreground">Install</span> below if offered.</li>
        <li>
          Or open the browser menu (⋮) and choose{" "}
          <span className="font-medium text-foreground">Install app</span> /{" "}
          <span className="font-medium text-foreground">Add to Home screen</span>.
        </li>
      </ol>
    );
  }
  return (
    <ol className="list-decimal pl-5 space-y-2 text-sm text-muted-foreground">
      <li>Open Circa in your phone’s browser.</li>
      <li>
        Use the browser menu to{" "}
        <span className="font-medium text-foreground">Add to Home Screen</span> /
        Install.
      </li>
    </ol>
  );
}

/**
 * How-to dialog + optional native install prompt. Shared by banner and settings.
 */
export function AddToHomeScreenDialog({ open, onOpenChange }) {
  const platform = getA2hsPlatform();
  const [canPrompt, setCanPrompt] = useState(false);

  useEffect(() => {
    listenForInstallPrompt();
    if (open) setCanPrompt(!!getDeferredInstallPrompt());
  }, [open]);

  const onInstall = async () => {
    const { outcome } = await promptInstall();
    if (outcome === "accepted") onOpenChange?.(false);
    setCanPrompt(!!getDeferredInstallPrompt());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add Circa to Home Screen</DialogTitle>
          <DialogDescription>
            Opens like an app (no browser bars)—much better for messaging.
            We’ll remove this tip once we ship a native app.
          </DialogDescription>
        </DialogHeader>
        <InstallSteps platform={platform} />
        <DialogFooter className="gap-2 sm:gap-0">
          {canPrompt ? (
            <Button type="button" onClick={onInstall}>
              <Download className="h-4 w-4 mr-1.5" />
              Install
            </Button>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange?.(false)}
          >
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Dismissible banner for Messages inboxes on phone. */
export function AddToHomeScreenBanner({ className }) {
  const isMobile = useIsMobile();
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    listenForInstallPrompt();
    setVisible(shouldShowA2hsBanner({ isMobile }));
  }, [isMobile]);

  if (!visible) return null;

  return (
    <>
      <div
        className={cn(
          "md:hidden flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm",
          className
        )}
      >
        <Download className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="font-medium leading-snug">Add Circa to your Home Screen</p>
          <p className="text-xs text-muted-foreground leading-snug">
            Full-screen chat without browser chrome. Temporary tip until the
            mobile app ships.
          </p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-8"
            onClick={() => setDialogOpen(true)}
          >
            How to add
          </Button>
        </div>
        <button
          type="button"
          className="shrink-0 p-1 rounded-md text-muted-foreground hover:bg-muted"
          aria-label="Dismiss"
          onClick={() => {
            dismissA2hs();
            setVisible(false);
          }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <AddToHomeScreenDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}

/** Compact card for Profile → Circa account (always available unless standalone). */
export function AddToHomeScreenSettingsCard({ className }) {
  const [standalone, setStandalone] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    listenForInstallPrompt();
    setStandalone(isStandaloneDisplay());
  }, []);

  if (standalone) return null;

  return (
    <>
      <div
        className={cn(
          "rounded-lg border border-border p-4 space-y-2",
          className
        )}
      >
        <div className="flex items-start gap-2">
          <Download className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium">Add Circa to Home Screen</p>
            <p className="text-xs text-muted-foreground">
              Better messaging on phone until we ship a native app. You can
              dismiss the Messages tip and still find this here.
            </p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setDialogOpen(true)}
        >
          How to add
        </Button>
      </div>
      <AddToHomeScreenDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
